import React, { useEffect } from 'react';
import { getSingleCharDiff } from '../lib/getSingleCharDiff';

import { Validator } from '../types';

export function useInput<ValueType extends string>(
  defaultValue?: ValueType,
  staticPrefix?: string,
) {
  const [prefix, setInnerPrefix] = React.useState<string>(staticPrefix || '');
  const initialValue = `${prefix}${defaultValue}` || '';
  const [dirty, setDirty] = React.useState(false);

  const [value, setInnerValue] = React.useState<ValueType | ''>(
    // @ts-ignore
    initialValue,
  );
  const [errors, setErrors] = React.useState<string[]>([]);

  function validate(validators: Validator[]) {
    setErrors(validators.map(v => v(value)).filter(isString));
  }

  function reset() {
    setInnerValue(defaultValue || '');
    setPrefix('');
    setErrors([]);
  }

  const setInnerValueAndDirty = React.useCallback(
    function setInnerValueAndDirty(val: string) {
      setInnerValue(val as ValueType);
      if (val !== prefix) setDirty(true);
    },
    [prefix],
  );

  // TODO switch away from taking in the event, then can remove persist
  function setValue(event: React.ChangeEvent<HTMLInputElement>) {
    // TODO is this needed?
    event.persist?.();

    // No white space
    const newValue = event.target.value.trim();

    const slugRe = /^[a-zA-Z0-9_\-.]+$/;

    const cashSlugRe = /^\${1}[a-zA-Z0-9_-]+/;

    if (!prefix || newValue.startsWith(prefix)) {
      // Handle pasting slug with leading and/or trailing whitespace
      setInnerValueAndDirty(`${prefix}${newValue.slice(prefix.length).trim()}`);
      // Handle select all then backspace
    } else if (!newValue && prefix) {
      setInnerValueAndDirty(prefix);
      // Handle paste just the slug
    } else if (newValue && newValue.match(slugRe)) {
      setInnerValueAndDirty(`${prefix}${newValue}`);
      // Handle pasting a cash slug with leading $
    } else if (newValue && newValue.match(cashSlugRe)) {
      // It's fine that this will work if someone pastes a "$someslug" in a
      // paypal or gofundme text input because we strip it anyway.
      setInnerValueAndDirty(`${prefix}${newValue.slice(1)}`);
    } else if (newValue) {
      // Handle single char diff in the middle of a prefix
      const diff = getSingleCharDiff(prefix, newValue);
      if (diff?.match(slugRe)) {
        setInnerValueAndDirty(`${prefix}${diff}`);
      }
    }
  }

  useEffect(() => {
    if (prefix && !value.startsWith(prefix)) {
      setInnerValue(prefix as ValueType);
    }
  }, [prefix, setInnerValue, value]);

  function setPrefix(val: string) {
    setInnerPrefix(val);
  }

  return {
    value,
    setValue,
    setInnerValue: setInnerValueAndDirty,
    initialValue,
    setPrefix,
    validate,
    errors,
    dirty,
    reset,
  };
}

function isString(value: string | null): value is string {
  return value !== null;
}
